(() => {
const gallerySelections = new WeakMap();
const galleryImages = new WeakMap();
const galleryAnimations = new WeakMap();
const allColorsImageBatches = new WeakMap();

function activateGalleryAnimation(root, panel) {
  const image = panel?.querySelector?.('img');
  const src = image?.dataset?.artboxAnimationSrc;
  if (!src || panel.hidden || !root.isConnected) return;
  const srcset = image.dataset.artboxAnimationSrcset || '';
  const isCurrent = () => root.isConnected && !panel.hidden && panel.querySelector('img') === image
    && image.dataset.artboxAnimationSrc === src && (image.dataset.artboxAnimationSrcset || '') === srcset;
  const apply = (record) => {
    if (!record.ready || record.applied || !isCurrent()) return;
    image.srcset = srcset;
    image.src = src;
    image.dataset.artboxAnimationState = 'playing';
    record.applied = true;
  };
  const existing = galleryAnimations.get(image);
  if (existing) { apply(existing); return; }
  const record = { ready: false, applied: false };
  galleryAnimations.set(image, record);
  image.dataset.artboxAnimationState = 'poster';
  const posterLoaded = image.complete
    ? Promise.resolve(image.naturalWidth > 0)
    : new Promise((resolve) => {
      image.addEventListener('load', () => resolve(true), { once: true });
      image.addEventListener('error', () => resolve(false), { once: true });
    });
  posterLoaded.then((loaded) => loaded && prepareGalleryImage(image, 'high')).then((loaded) => {
    if (!loaded || !isCurrent()) { galleryAnimations.delete(image); return; }
    const start = () => {
      if (!isCurrent()) { galleryAnimations.delete(image); return; }
      const animation = new window.Image();
      record.image = animation;
      image.dataset.artboxAnimationState = 'loading';
      animation.fetchPriority = 'low';
      animation.decoding = 'async';
      animation.addEventListener('error', () => {
        image.dataset.artboxAnimationState = 'error';
      }, { once: true });
      animation.addEventListener('load', async () => {
        try {
          if (typeof animation.decode === 'function') await animation.decode();
          record.ready = true;
          apply(record);
        } catch {
          image.dataset.artboxAnimationState = 'error';
        }
      }, { once: true });
      animation.sizes = image.sizes;
      animation.srcset = srcset;
      animation.src = src;
    };
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(start));
    } else window.setTimeout(start, 0);
  });
}

function prepareGalleryImage(image, priority = 'low') {
  if (!image) return Promise.resolve(true);
  if (priority === 'high' || image.fetchPriority !== 'high') image.fetchPriority = priority;
  image.loading = 'eager';
  if (galleryImages.has(image)) return galleryImages.get(image);
  const ready = typeof image.decode === 'function'
    ? image.decode().then(() => true, () => false)
    : new Promise((resolve) => {
      if (image.complete) { resolve(image.naturalWidth > 0); return; }
      image.addEventListener('load', () => resolve(true), { once: true });
      image.addEventListener('error', () => resolve(false), { once: true });
    });
  galleryImages.set(image, ready);
  ready.then((loaded) => { if (!loaded) galleryImages.delete(image); });
  return ready;
}

function commitGalleryItem(root, target) {
  for (const panel of root.querySelectorAll('[data-artbox-gallery-panel]')) {
    const active = panel.dataset.artboxGalleryPanel === target;
    panel.hidden = !active;
    panel.classList.toggle('is-active', active);
  }

  for (const thumb of root.querySelectorAll('[data-artbox-gallery-thumb]')) {
    const active = thumb.dataset.artboxGalleryThumb === target;
    thumb.setAttribute('aria-selected', String(active));
    thumb.tabIndex = active ? 0 : -1;
  }
  const activePanel = Array.from(root.querySelectorAll('[data-artbox-gallery-panel]'))
    .find((panel) => !panel.hidden);
  activateGalleryAnimation(root, activePanel);
}

function selectGalleryItem(root, target) {
  const selection = {};
  gallerySelections.set(root, selection);
  const panel = Array.from(root.querySelectorAll('[data-artbox-gallery-panel]'))
    .find((panel) => panel.dataset.artboxGalleryPanel === target);
  if (!panel) return;
  const image = panel.querySelector?.('img');
  if (!image) { commitGalleryItem(root, target); return; }
  prepareGalleryImage(image, 'high').then((loaded) => {
    if (loaded && gallerySelections.get(root) === selection && root.isConnected) {
      commitGalleryItem(root, target);
    }
  });
}

function selectGalleryMedia(root, mediaId) {
  if (!mediaId) return;
  const matchingThumb = Array.from(root.querySelectorAll('[data-artbox-gallery-thumb]'))
    .find((thumb) => thumb.dataset.artboxMediaId === String(mediaId));
  if (matchingThumb) selectGalleryItem(root, matchingThumb.dataset.artboxGalleryThumb);
}

function findStorySource(gallery) {
  const productId = gallery.dataset.artboxProductId;
  if (!productId) return null;
  const ownerDocument = gallery.ownerDocument ?? document;
  const sources = ownerDocument.querySelectorAll?.('[data-artbox-story-source]') ?? [];
  return Array.from(sources).find((source) => source.dataset.artboxProductId === productId) ?? null;
}

function initialiseGallery(root) {
  const thumbs = Array.from(root.querySelectorAll('[data-artbox-gallery-thumb]'));
  if (thumbs.length === 0) return;

  root.dataset.artboxGalleryInitialised = 'true';
  activateGalleryAnimation(root, Array.from(root.querySelectorAll('[data-artbox-gallery-panel]'))
    .find((panel) => !panel.hidden));

  if (root.dataset.artboxGalleryWarmup !== 'true') {
    root.dataset.artboxGalleryWarmup = 'true';
    const panels = Array.from(root.querySelectorAll('[data-artbox-gallery-panel]'));
    const initialImage = panels.find((panel) => !panel.hidden)?.querySelector?.('img');
    const warmNext = () => {
      const warm = () => {
        if (root.isConnected) prepareGalleryImage(panels[1]?.querySelector?.('img'));
      };
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(warm);
      else window.setTimeout(warm, 300);
    };
    if (initialImage?.complete && initialImage.naturalWidth > 0) warmNext();
    else initialImage?.addEventListener('load', warmNext, { once: true });
  }

  for (const thumb of thumbs) {
    if (thumb.dataset.artboxGalleryControlInitialised === 'true') continue;
    thumb.dataset.artboxGalleryControlInitialised = 'true';

    thumb.addEventListener('click', () => {
      selectGalleryItem(root, thumb.dataset.artboxGalleryThumb);
    });

    thumb.addEventListener('keydown', (event) => {
      const currentThumbs = Array.from(root.querySelectorAll('[data-artbox-gallery-thumb]'));
      let nextIndex;

      if (event.key === 'ArrowLeft') nextIndex = (currentThumbs.indexOf(thumb) - 1 + currentThumbs.length) % currentThumbs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentThumbs.indexOf(thumb) + 1) % currentThumbs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = currentThumbs.length - 1;

      if (nextIndex === undefined) return;

      event.preventDefault();
      const nextThumb = currentThumbs[nextIndex];
      selectGalleryItem(root, nextThumb.dataset.artboxGalleryThumb);
      nextThumb.focus();
    });
  }

  const featured = root.closest?.('[data-product-featured]');
  if (!featured) return;

  const field = featured.querySelector('select[name="id"]');
  let selectedMediaId = field?.querySelector?.('option:checked[data-mdid]')?.dataset.mdid;
  if (!selectedMediaId && field) {
    try {
      const variants = JSON.parse(featured.querySelector('.pr_variants_json')?.textContent || '[]');
      const variant = variants.find((variant) => String(variant.id) === field.value);
      selectedMediaId = variant?.featured_media?.id ?? variant?.featured_image?.id;
    } catch { /* Leave the visible image in place when variant data is unavailable. */ }
  }
  selectGalleryMedia(root, selectedMediaId);

  if (root.dataset.artboxVariantGalleryInitialised === 'true') return;
  root.dataset.artboxVariantGalleryInitialised = 'true';
  const handleVariantChange = (event) => {
    const variant = event.currentVariant ?? event.detail?.currentVariant ?? event.detail?.variant;
    selectGalleryMedia(root, variant?.featured_media?.id ?? variant?.featured_image?.id);
  };

  if (typeof window.jQuery_T4NT === 'function') {
    window.jQuery_T4NT(featured).on('variant:changed.artboxGallery', handleVariantChange);
  } else {
    featured.addEventListener?.('variant:changed', handleVariantChange);
  }
}

function initialiseGalleries(scope = document) {
  const galleries = [];
  if (scope.matches?.('[data-artbox-gallery]')) galleries.push(scope);
  galleries.push(...scope.querySelectorAll('[data-artbox-gallery]'));
  galleries.forEach(initialiseGallery);
}

function initialisePaymentDetails(root) {
  const trigger = root.querySelector('[data-artbox-payment-open]');
  const dialog = root.querySelector('[data-artbox-payment-dialog]');
  if (!trigger || !dialog || trigger.dataset.artboxPaymentReady === 'true') return;
  trigger.dataset.artboxPaymentReady = 'true';

  const beginInteraction = bindProductDialogFocus(dialog, trigger);
  trigger.addEventListener('click', (event) => {
    beginInteraction(event);
    dialog.showModal();
  });
  dialog.querySelector('[data-artbox-payment-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
}

function bindProductDialogFocus(dialog, trigger) {
  let keyboardInteraction = false;
  dialog.addEventListener('keydown', () => { keyboardInteraction = true; });
  dialog.addEventListener('pointerdown', () => { keyboardInteraction = false; });
  dialog.addEventListener('close', () => {
    trigger.focus({ preventScroll: true });
    // Preserve keyboard return focus; pointer browsing should leave no focus ring.
    if (!keyboardInteraction) trigger.blur();
  });
  return (event) => { keyboardInteraction = event.detail === 0; };
}

(function initialiseProductLifecycle() {
  const lifecycleKey = Symbol.for('artbox.product.gallery.lifecycle');
  const existingLifecycle = window[lifecycleKey];

  if (existingLifecycle) {
    existingLifecycle.initialise(document);
    return;
  }

  // Capture document-navigation intent once; Kalles may replace the current URL later.
  const incomingNavigationUrl = (() => {
    let navigationName;
    try {
      navigationName = window.performance?.getEntriesByType?.('navigation')?.[0]?.name;
    } catch (_error) {
      // Navigation timing is unavailable in some environments.
    }
    try {
      return new URL(navigationName || window.location?.href);
    } catch (_error) {
      return null;
    }
  })();
  const incomingNavigationHasVariant = incomingNavigationUrl?.searchParams.has('variant') || false;
  const incomingNavigationRequiresDevice = incomingNavigationUrl?.searchParams.get('artbox_choose_device') === '1';
  const incomingVariantConfirmsDevice = incomingNavigationHasVariant && !incomingNavigationRequiresDevice;
  const incomingDeviceModels = incomingNavigationUrl?.searchParams.getAll('artbox_device') || [];
  const incomingCollectionDevice = !incomingNavigationHasVariant && !incomingNavigationRequiresDevice && incomingDeviceModels.length === 1
    ? incomingDeviceModels[0].trim() : '';

  const colorNavigationRecords = new Map();
  const colorLayoutRecords = new Map();
  let colorLayoutObserver;
  let colorLayoutFrame = null;
  const colorScrollbarRecords = new Map();
  const pendingColorScrollbars = new Set();
  let colorScrollbarObserver;
  let colorScrollbarFrame = null;

  function colorScrollPosition(record) {
    const position = record.rail.scrollLeft * (record.rtl ? -1 : 1);
    return Math.max(0, Math.min(record.maxScroll, position));
  }

  function flushColorScrollbars() {
    colorScrollbarFrame = null;
    const updates = [];
    // Cache layout on resize; native scroll events only read the scroll position.
    for (const record of pendingColorScrollbars) {
      if (colorScrollbarRecords.get(record.rail) !== record || record.rail.isConnected === false) continue;
      if (record.measure) {
        const viewport = record.rail.clientWidth;
        const content = record.rail.scrollWidth;
        record.maxScroll = Math.max(0, content - viewport);
        record.ratio = content > 0 ? Math.min(1, viewport / content) : 1;
        record.visible = viewport > 0 && record.maxScroll > 1;
        record.rtl = window.getComputedStyle?.(record.rail).direction === 'rtl';
        record.measure = false;
      }
      const progress = record.maxScroll > 0 ? colorScrollPosition(record) / record.maxScroll : 0;
      const offset = (1 - record.ratio) * (record.rtl ? 1 - progress : progress);
      updates.push({ record, width: `${record.ratio * 100}%`,
        transform: `translateX(${record.ratio > 0 ? offset / record.ratio * 100 : 0}%)` });
    }
    pendingColorScrollbars.clear();
    for (const { record, width, transform } of updates) {
      record.track.hidden = !record.visible;
      const state = record.visible ? 'visible' : 'hidden';
      if (record.rail.dataset.artboxColorScrollbar !== state) record.rail.dataset.artboxColorScrollbar = state;
      if (record.viewport && record.viewport.dataset.artboxColorScrollbar !== state) record.viewport.dataset.artboxColorScrollbar = state;
      if (record.thumb.style.width !== width) record.thumb.style.width = width;
      if (record.thumb.style.transform !== transform) record.thumb.style.transform = transform;
    }
  }

  function scheduleColorScrollbar(record, measure = false) {
    record.measure ||= measure;
    pendingColorScrollbars.add(record);
    if (colorScrollbarFrame != null) return;
    if (typeof window.requestAnimationFrame === 'function') {
      colorScrollbarFrame = window.requestAnimationFrame(flushColorScrollbars);
    } else flushColorScrollbars();
  }

  function initialiseColorScrollbars(root) {
    for (const track of root.querySelectorAll('[data-artbox-color-scrollbar]')) {
      const sibling = track.previousElementSibling;
      const viewport = sibling?.matches('[data-artbox-color-viewport]') ? sibling : null;
      const rail = viewport?.firstElementChild ?? sibling;
      const thumb = track.firstElementChild;
      if (!rail?.matches('[data-artbox-linked-colors]') || !thumb || colorScrollbarRecords.has(rail)) continue;
      const record = { root, rail, viewport, track, thumb, maxScroll: 0, ratio: 1, rtl: false, drag: null };
      const setPosition = (position) => {
        rail.scrollLeft = Math.max(0, Math.min(record.maxScroll, position)) * (record.rtl ? -1 : 1);
        scheduleColorScrollbar(record);
      };
      record.scroll = () => scheduleColorScrollbar(record);
      record.resize = () => scheduleColorScrollbar(record, true);
      record.pointerdown = (event) => {
        if (event.button !== 0 || event.isPrimary === false || !record.visible || record.drag) return;
        const bounds = track.getBoundingClientRect();
        const travel = bounds.width * (1 - record.ratio);
        if (travel <= 0) return;
        const position = colorScrollPosition(record);
        const progress = position / record.maxScroll;
        const thumbLeft = bounds.left + travel * (record.rtl ? 1 - progress : progress);
        if (event.clientX < thumbLeft || event.clientX > thumbLeft + bounds.width * record.ratio) {
          const fraction = Math.max(0, Math.min(1, (event.clientX - bounds.left - bounds.width * record.ratio / 2) / travel));
          setPosition(record.maxScroll * (record.rtl ? 1 - fraction : fraction));
        }
        record.drag = { pointerId: event.pointerId, x: event.clientX, position: colorScrollPosition(record), travel };
        track.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      };
      record.pointermove = (event) => {
        const drag = record.drag;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const delta = (event.clientX - drag.x) / drag.travel * record.maxScroll;
        setPosition(drag.position + delta * (record.rtl ? -1 : 1));
      };
      record.pointerend = (event) => {
        if (record.drag?.pointerId !== event.pointerId) return;
        record.drag = null;
        track.releasePointerCapture?.(event.pointerId);
      };
      colorScrollbarRecords.set(rail, record);
      rail.addEventListener('scroll', record.scroll, { passive: true });
      track.addEventListener('pointerdown', record.pointerdown);
      track.addEventListener('pointermove', record.pointermove);
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) track.addEventListener(type, record.pointerend);
      if (typeof window.ResizeObserver === 'function') {
        colorScrollbarObserver ??= new window.ResizeObserver((entries) => {
          for (const { target } of entries) {
            const pending = colorScrollbarRecords.get(target);
            if (pending) scheduleColorScrollbar(pending, true);
          }
        });
        colorScrollbarObserver.observe(rail);
      } else window.addEventListener?.('resize', record.resize, { passive: true });
      scheduleColorScrollbar(record, true);
    }
  }

  function releaseColorScrollbars(scope) {
    for (const [rail, record] of colorScrollbarRecords) {
      if (record.root !== scope && !scope.contains?.(record.root)) continue;
      rail.removeEventListener('scroll', record.scroll);
      window.removeEventListener?.('resize', record.resize);
      record.track.removeEventListener('pointerdown', record.pointerdown);
      record.track.removeEventListener('pointermove', record.pointermove);
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) record.track.removeEventListener(type, record.pointerend);
      if (record.drag) record.track.releasePointerCapture?.(record.drag.pointerId);
      record.drag = null;
      colorScrollbarObserver?.unobserve(rail);
      pendingColorScrollbars.delete(record);
      colorScrollbarRecords.delete(rail);
      // Leave the native scrollbar available while the section is uninitialised.
      delete rail.dataset.artboxColorScrollbar;
      if (record.viewport) delete record.viewport.dataset.artboxColorScrollbar;
      record.track.hidden = true;
    }
    if (colorScrollbarRecords.size === 0) {
      if (colorScrollbarFrame != null) window.cancelAnimationFrame?.(colorScrollbarFrame);
      colorScrollbarFrame = null;
      colorScrollbarObserver?.disconnect();
      colorScrollbarObserver = undefined;
    }
  }

  function alignLinkedColors(entries) {
    const alignments = [];
    // ResizeObserver runs after layout. Complete all reads before scrolling any rail.
    for (const { target: rail, contentRect } of entries) {
      if (!colorLayoutRecords.has(rail) || rail.isConnected === false) continue;
      const chosen = rail.querySelector('a[aria-current="page"]')?.closest('.t4s-pr-choose__item');
      if (!chosen || (contentRect?.width ?? rail.clientWidth) === 0) continue;
      const bounds = rail.getBoundingClientRect();
      const current = chosen.getBoundingClientRect();
      const leftOverflow = current.left - bounds.left - 5;
      const rightOverflow = current.right - bounds.right + 5;
      const offset = leftOverflow < 0 ? leftOverflow : rightOverflow > 0 ? rightOverflow : 0;
      alignments.push({ rail, scrollLeft: offset ? rail.scrollLeft + offset : null });
    }
    for (const { rail, scrollLeft } of alignments) {
      rail.dataset.artboxLinkedColorsReady = 'true';
      colorLayoutObserver?.unobserve(rail);
      colorLayoutRecords.delete(rail);
      // Scroll only the color rail, preserving the selected outline's five-pixel space.
      if (scrollLeft !== null) rail.scrollLeft = scrollLeft;
    }
  }

  function initialiseLinkedColors(root) {
    for (const rail of root.querySelectorAll('[data-artbox-linked-colors]')) {
      if (rail.dataset.artboxLinkedColorsReady === 'true' || colorLayoutRecords.has(rail)) continue;
      if (!rail.querySelector('a[aria-current="page"]')) continue;
      colorLayoutRecords.set(rail, root);
      if (typeof window.ResizeObserver === 'function') {
        colorLayoutObserver ??= new window.ResizeObserver(alignLinkedColors);
        colorLayoutObserver.observe(rail);
      }
    }
    if (colorLayoutObserver || colorLayoutRecords.size === 0 || colorLayoutFrame != null) return;
    const alignPending = () => {
      colorLayoutFrame = null;
      alignLinkedColors([...colorLayoutRecords.keys()].map((target) => ({ target })));
    };
    if (typeof window.requestAnimationFrame === 'function') {
      colorLayoutFrame = window.requestAnimationFrame(alignPending);
    } else alignPending();
  }

  function enhanceColorNavigation(root) {
    if (root.dataset.artboxColorPrefetch === 'bounded') return;
    if (!window.HTMLScriptElement?.supports?.('speculationrules')
        || typeof window.IntersectionObserver !== 'function'
        || window.Shopify?.designMode) return;
    const connection = window.navigator?.connection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;

    for (const rail of root.querySelectorAll('[data-artbox-linked-colors]')) {
      if (colorNavigationRecords.has(rail)) continue;
      const record = { root, rail, visible: false, timer: null, script: null };
      const prepare = () => {
        window.clearTimeout(record.timer);
        if (!record.visible || record.script || document.readyState !== 'complete') return;
        record.timer = window.setTimeout(() => {
          if (!rail.isConnected || document.visibilityState === 'hidden' || !record.visible) return;
          const connection = window.navigator?.connection;
          if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
          const links = Array.from(rail.querySelectorAll('a[href]'));
          const selected = links.findIndex(link => link.getAttribute('aria-current') === 'page');
          if (selected < 0) return;
          const bounds = rail.getBoundingClientRect();
          const candidates = links.map((link, index) => ({ link, distance: Math.abs(index - selected) }))
            .filter(({ link, distance }) => {
              if (!distance) return false;
              const box = link.getBoundingClientRect();
              return box.right > bounds.left && box.left < bounds.right;
            }).sort((a, b) => a.distance - b.distance);
          const urls = [];
          for (const { link } of candidates) {
            const url = new URL(link.href, window.location.href);
            if (url.origin !== window.location.origin || !url.pathname.includes('/products/')
                || url.href === window.location.href || urls.includes(url.href)) continue;
            urls.push(url.href);
            if (urls.length === 2) break;
          }
          if (!urls.length) return;
          // Fetch HTML only: do not run destination analytics, cart or COD scripts.
          const script = document.createElement('script');
          script.type = 'speculationrules';
          script.dataset.artboxColorPrefetch = '';
          script.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls, eagerness: 'immediate' }] });
          document.head.appendChild(script);
          record.script = script;
          record.observer.disconnect();
          rail.removeEventListener('scroll', prepare);
          window.removeEventListener('load', prepare);
        }, 700);
      };
      record.prepare = prepare;
      record.observer = new window.IntersectionObserver(entries => {
        record.visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5);
        prepare();
      }, { threshold: [0, 0.5] });
      colorNavigationRecords.set(rail, record);
      record.observer.observe(rail);
      rail.addEventListener('scroll', prepare, { passive: true });
      window.addEventListener('load', prepare, { once: true });
    }
  }

  const rememberedOptions = new Map([
    ['device', 'preferredDeviceModel'],
    ['case type', 'preferredCaseType'],
    ['style', 'preferredStyle'],
  ]);

  const conditionalDescriptions = new Map([
    ['glass', '.description-1'],
    ['liquid silicone', '.description-2'],
    ['silver plated', '.description-3'],
    ['standard', '.description-standard'],
    ['with kickstand', '.description-kickstand'],
  ]);

  function normalizeOptionName(value) {
    return String(value || '').trim().toLowerCase();
  }

  function readProductPricingData(record) {
    if (record.pricingData) return record.pricingData;
    try {
      const variants = JSON.parse(record.root.querySelector('.pr_variants_json')?.textContent || '[]');
      const options = JSON.parse(record.root.querySelector('.pr_options_json')?.textContent || '[]');
      record.pricingData = { variants, options };
    } catch (_error) {
      record.pricingData = { variants: [], options: [] };
    }
    return record.pricingData;
  }

  function rememberViewedColor(record) {
    const root = record?.root;
    const group = root?.dataset?.artboxColorMemoryGroup;
    if (!group || root.dataset.artboxOptionsReady !== 'true' || root.isConnected === false
      || record.restoringCollectionDevice || typeof window.ArtboxColorMemory?.write !== 'function') return;

    const { variants, options } = readProductPricingData(record);
    if (!Array.isArray(variants) || !Array.isArray(options)) return;
    const caseIndex = options.findIndex((option) => normalizeOptionName(option.name) === 'case type');
    const nativeColor = root.dataset.artboxColorMemoryNative === 'true';
    const colorIndex = nativeColor
      ? options.findIndex((option) => ['color', 'colour'].includes(normalizeOptionName(option.name))) : -1;
    const variantId = root.querySelector('select[name="id"], input[name="id"]')?.value;
    const variant = variants.find((candidate) => String(candidate.id) === String(variantId));
    if ((caseIndex >= 0 || nativeColor) && !variant) return;
    const values = variant?.options || options.map((_option, index) => variant?.[`option${index + 1}`]);
    if (variant && options.some((_option, index) => {
      const selected = root.querySelector(`[data-swatch-option][data-id="${index}"]`)
        ?.querySelector('[data-swatch-item].is--selected')?.getAttribute('data-value');
      return selected !== undefined && selected !== null && selected !== values[index];
    })) return;

    const caseType = caseIndex >= 0 ? String(values[caseIndex] || '').trim() : '';
    if (caseIndex >= 0 && !caseType) return;
    let colorId = String(root.dataset.artboxProductId || '').trim();
    if (nativeColor) {
      const color = colorIndex >= 0 ? String(values[colorIndex] || '') : '';
      if (!color.trim()) return;
      colorId = `color:${color}`;
    }
    if (colorId) window.ArtboxColorMemory.write(group, caseType, colorId);
  }

  function formatProductMoney(root, cents) {
    const themeFormatter = window.T4SThemeSP?.Currency?.formatMoney;
    if (typeof themeFormatter === 'function') return themeFormatter(cents);
    try {
      return new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: root.dataset.artboxCurrency || 'PHP',
      }).format(cents / 100);
    } catch (_error) {
      return `₱${(cents / 100).toFixed(2)}`;
    }
  }

  function hasPlainPriceText(node, text) {
    return node?.childNodes?.length === 1
      && node.firstChild?.nodeType === 3
      && node.textContent === text;
  }

  function syncUnconfirmedPrice(record) {
    if (record.deviceConfirmed) return;
    const priceRoot = record.root.querySelector?.('[data-artbox-price] .t4s-product-price');
    if (!priceRoot) return;

    const { variants, options } = readProductPricingData(record);
    if (variants.length === 0 || options.length === 0) return;
    const selectedOptions = new Map();
    for (const option of record.root.querySelectorAll('[data-swatch-option]')) {
      const name = option.querySelector('.t4s-swatch__title')?.textContent?.split(':')[0];
      const value = option.querySelector('[data-swatch-item].is--selected')?.getAttribute('data-value')?.trim();
      if (value) selectedOptions.set(normalizeOptionName(name), value);
    }

    const matchingVariants = variants.filter((variant) => variant.available && options.every((option, index) => {
      if (normalizeOptionName(option.name) === 'device') return true;
      const selected = selectedOptions.get(normalizeOptionName(option.name));
      const variantValue = variant.options?.[index] ?? variant[`option${index + 1}`];
      return !selected || variantValue === selected;
    }));
    if (matchingVariants.length === 0) return;

    const prices = matchingVariants.map((variant) => Number(variant.price)).filter(Number.isFinite);
    if (prices.length === 0) return;
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    if (minPrice !== maxPrice) {
      const text = `${formatProductMoney(record.root, minPrice)} – ${formatProductMoney(record.root, maxPrice)}`;
      if (!hasPlainPriceText(priceRoot, text)) priceRoot.textContent = text;
      return;
    }

    const comparePrices = [...new Set(matchingVariants
      .map((variant) => Number(variant.compare_at_price))
      .filter((comparePrice) => Number.isFinite(comparePrice) && comparePrice > minPrice))];
    if (comparePrices.length === 1) {
      const compareText = formatProductMoney(record.root, comparePrices[0]);
      const currentText = formatProductMoney(record.root, minPrice);
      const [existingCompare, existingCurrent] = priceRoot.childNodes ?? [];
      if (priceRoot.childNodes?.length === 2
        && existingCompare?.nodeName === 'DEL' && existingCurrent?.nodeName === 'INS'
        && existingCompare.attributes.length === 0 && existingCurrent.attributes.length === 0
        && hasPlainPriceText(existingCompare, compareText)
        && hasPlainPriceText(existingCurrent, currentText)) return;

      const ownerDocument = record.root.ownerDocument ?? document;
      const compare = ownerDocument.createElement('del');
      const current = ownerDocument.createElement('ins');
      compare.textContent = compareText;
      current.textContent = currentText;
      priceRoot.replaceChildren(compare, current);
      return;
    }
    const text = formatProductMoney(record.root, minPrice);
    if (!hasPlainPriceText(priceRoot, text)) priceRoot.textContent = text;
  }

  function readRememberedOption(storageKey) {
    try {
      return window.localStorage.getItem(storageKey);
    } catch (_error) {
      return null;
    }
  }

  function writeRememberedOption(storageKey, value) {
    try {
      window.localStorage.setItem(storageKey, value);
    } catch (_error) {
      return;
    }
  }

  function rememberSelectedOption(option) {
    const title = option.querySelector('.t4s-swatch__title')?.textContent?.split(':')[0];
    const storageKey = rememberedOptions.get(normalizeOptionName(title));
    const selected = option.querySelector('[data-swatch-item].is--selected')?.getAttribute('data-value');
    if (storageKey && selected) writeRememberedOption(storageKey, selected.trim());
  }

  function syncSelectedVariantUrl(root) {
    const variantField = root.querySelector?.('select[name="id"], input[name="id"]');
    const variantId = String(variantField?.value || '').trim();
    if (!variantId || typeof window.history?.replaceState !== 'function') return;

    try {
      const nextUrl = new URL(window.location.href);
      const clearDeviceChoice = nextUrl.searchParams.has('artbox_choose_device') && deviceIsConfirmed(root);
      if (nextUrl.searchParams.get('variant') === variantId && !clearDeviceChoice) return;
      nextUrl.searchParams.set('variant', variantId);
      if (clearDeviceChoice) nextUrl.searchParams.delete('artbox_choose_device');
      window.history.replaceState(window.history.state, '', nextUrl.toString());
    } catch (_error) {
      // A malformed or unavailable location must not block option selection.
    }
  }

  function syncPurchaseLabels(root) {
    const easySellTitle = root.querySelector?.('#es-popup-button .es-button-title');
    if (easySellTitle && easySellTitle.textContent !== 'Buy Now') easySellTitle.textContent = 'Buy Now';

    const addToBag = root.querySelector?.('[data-atc-form] .t4s-btn-atc_text');
    const nativeAddToCart = window.T4SProductStrings?.addToCart;
    if (addToBag && (!nativeAddToCart || addToBag.textContent.trim() === nativeAddToCart.trim())) {
      addToBag.textContent = 'Add to Bag';
    }
  }

  function syncUnconfirmedPurchaseControls(record, deviceRequired) {
    const addToBag = record.root.querySelector?.('[data-atc-form]');
    const buyNow = record.root.querySelector?.('#es-popup-button');

    if (deviceRequired) {
      if (addToBag) {
        addToBag.disabled = false;
        addToBag.removeAttribute?.('disabled');
        addToBag.removeAttribute?.('aria-disabled');
        addToBag.dataset.artboxDeviceGate = 'true';
      }
      if (buyNow) {
        const title = buyNow.querySelector?.('.es-button-title');
        if (title && title.textContent !== 'Buy Now') title.textContent = 'Buy Now';
        buyNow.removeAttribute?.('disabled');
        buyNow.removeAttribute?.('aria-disabled');
        buyNow.dataset.artboxDeviceGate = 'true';
      }
      return;
    }

    if (addToBag?.dataset.artboxDeviceGate) delete addToBag.dataset.artboxDeviceGate;
    if (buyNow?.dataset.artboxDeviceGate) delete buyNow.dataset.artboxDeviceGate;
  }

  function waitForKallesUpdate() {
    return new Promise((resolve) => {
      const schedule = typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => callback();
      schedule(() => schedule(resolve));
    });
  }

  async function restoreRememberedOptions(root, record) {
    if (incomingNavigationHasVariant || incomingNavigationRequiresDevice) return;

    if (incomingCollectionDevice && root.dataset.artboxEarlyDeviceConfirmed !== 'true') {
      await restoreCollectionDevice(root, record);
    }

    for (const option of root.querySelectorAll('[data-swatch-option]')) {
      if (optionRecords.get(root) !== record) return;
      const title = option.querySelector('.t4s-swatch__title')?.textContent?.split(':')[0];
      const normalizedTitle = normalizeOptionName(title);
      if (incomingCollectionDevice && normalizedTitle === 'device') continue;
      const storageKey = rememberedOptions.get(normalizedTitle);
      const remembered = storageKey ? readRememberedOption(storageKey) : null;
      if (incomingCollectionDevice && remembered) {
        const { variants, options } = readProductPricingData(record);
        const deviceIndex = options.findIndex((candidate) => normalizeOptionName(candidate.name) === 'device');
        const optionIndex = options.findIndex((candidate) => normalizeOptionName(candidate.name) === normalizedTitle);
        const selected = options.map((_candidate, index) => root.querySelector(
          `[data-swatch-option][data-id="${index}"] [data-swatch-item].is--selected`,
        )?.getAttribute('data-value'));
        if (!variants.some((variant) => options.every((_candidate, index) => {
          const value = variant.options?.[index] ?? variant[`option${index + 1}`];
          if (index === deviceIndex) return value === incomingCollectionDevice;
          if (index === optionIndex) return value === remembered;
          return !selected[index] || value === selected[index];
        }))) continue;
      }
      const target = remembered
        ? [...option.querySelectorAll('[data-swatch-item]')].find((item) =>
          item.getAttribute('data-value')?.trim() === remembered
          && (normalizedTitle === 'device' || !item.classList.contains('is--soldout'))
          && !item.classList.contains('is--unavailable'))
        : null;
      if (target && !target.classList.contains('is--selected')) {
        if (normalizedTitle === 'device') {
          const { variants, options } = readProductPricingData(record);
          const selected = options.map((_candidate, index) => root.querySelector(
            `[data-swatch-option][data-id="${index}"] [data-swatch-item].is--selected`,
          )?.getAttribute('data-value'));
          const retained = variants.find((variant) => options.every((candidate, index) => {
            const value = variant.options?.[index] ?? variant[`option${index + 1}`];
            return normalizeOptionName(candidate.name) === 'device'
              ? value === remembered : !selected[index] || value === selected[index];
          }));
          retainSoldoutDeviceVariant(root, retained);
        }
        target.click();
        await waitForKallesUpdate();
      }
    }
    if (incomingCollectionDevice && optionRecords.get(root) === record) {
      syncCollectionDeviceConfirmation(root, record);
    }
  }

  function syncCollectionDeviceConfirmation(root, record) {
    const { variants, options } = readProductPricingData(record);
    const deviceIndex = options.findIndex((option) => normalizeOptionName(option.name) === 'device');
    const variantId = root.querySelector('select[name="id"], input[name="id"]')?.value;
    const selected = options.map((_option, index) => root.querySelector(
      `[data-swatch-option][data-id="${index}"] [data-swatch-item].is--selected`,
    )?.getAttribute('data-value'));
    const compatible = deviceIndex >= 0 && selected[deviceIndex] === incomingCollectionDevice
      && variants.some((variant) => String(variant.id) === String(variantId)
        && options.every((_option, index) => (variant.options?.[index] ?? variant[`option${index + 1}`]) === selected[index]));
    if (!compatible) {
      record.deviceConfirmed = false;
      return false;
    }
    confirmDevice(record);
    syncSelectedVariantUrl(root);
    return true;
  }

  async function restoreCollectionDevice(root, record) {
    const { variants, options } = readProductPricingData(record);
    const deviceIndex = options.findIndex((option) => normalizeOptionName(option.name) === 'device');
    if (deviceIndex < 0) return;
    const groups = options.map((_option, index) => root.querySelector(`[data-swatch-option][data-id="${index}"]`));
    if (groups.some((group) => !group)) return;
    const values = (variant) => variant.options || options.map((_option, index) => variant[`option${index + 1}`]);
    const chosen = groups.map((group) => group.querySelector('[data-swatch-item].is--selected')?.getAttribute('data-value'));
    const candidates = variants.filter((variant) => values(variant)[deviceIndex] === incomingCollectionDevice);
    const targetVariant = candidates.find((variant) => values(variant).every((value, index) =>
      index === deviceIndex || !chosen[index] || value === chosen[index]))
      || candidates.find((variant) => variant.available) || candidates[0];
    if (!targetVariant) return;
    retainSoldoutDeviceVariant(root, targetVariant);

    // Let the ready selector own variant updates; choose other options before Device
    // so its final selection cannot fall back to an incompatible model.
    const indices = options.map((_option, index) => index).filter((index) => index !== deviceIndex);
    indices.push(deviceIndex);
    record.restoringCollectionDevice = true;
    try {
      for (const index of indices) {
        if (optionRecords.get(root) !== record) return;
        const target = Array.from(groups[index].querySelectorAll('[data-swatch-item]'))
          .find((item) => item.getAttribute('data-value') === values(targetVariant)[index]);
        if (!target) return;
        if (!target.classList.contains('is--selected')) {
          target.click();
          await waitForKallesUpdate();
        }
      }
      if (optionRecords.get(root) !== record) return;
      if (!syncCollectionDeviceConfirmation(root, record)) return;
      writeRememberedOption('preferredDeviceModel', incomingCollectionDevice);
    } finally {
      record.restoringCollectionDevice = false;
    }
  }

  function retainSoldoutDeviceVariant(root, variant) {
    if (!variant || variant.available) return;
    // A hidden option must remain selectable for the native selector to retain
    // this exact model. Stock availability still controls both purchase paths.
    const field = root.querySelector('select[name="id"]');
    const slot = Array.from(field?.options || []).find((option) => String(option.value) === String(variant.id));
    if (slot) slot.disabled = false;
  }

  function syncConditionalDescriptions(root) {
    const storySource = findStorySource(root);
    if (!storySource) return;

    const descriptions = new Map();
    for (const selector of new Set(conditionalDescriptions.values())) {
      const matches = Array.from(storySource.querySelectorAll(selector));
      if (matches.length > 0) descriptions.set(selector, matches);
    }
    if (descriptions.size === 0) return;

    const selectedValues = Array.from(root.querySelectorAll('[data-swatch-item].is--selected'))
      .map((item) => normalizeOptionName(item.getAttribute('data-value')));
    const selectedSelectors = new Set(
      selectedValues.map((value) => conditionalDescriptions.get(value)).filter(Boolean),
    );
    const numberedSelectors = ['.description-1', '.description-2', '.description-3'];
    const caseTypeSelectors = ['.description-standard', '.description-kickstand'];

    if (!numberedSelectors.some((selector) => selectedSelectors.has(selector))) {
      const fallback = numberedSelectors.find((selector) => descriptions.has(selector));
      if (fallback) selectedSelectors.add(fallback);
    }
    if (!caseTypeSelectors.some((selector) => selectedSelectors.has(selector))
      && descriptions.has('.description-standard')) {
      selectedSelectors.add('.description-standard');
    }

    const visibleDescriptions = new Set();
    for (const selector of selectedSelectors) {
      descriptions.get(selector)?.forEach((description) => visibleDescriptions.add(description));
    }
    // Reconciliation runs during initialization and variant handoff. Apply only
    // the final visibility so unchanged sections do not invalidate layout again.
    for (const matches of descriptions.values()) {
      for (const description of matches) {
        const display = visibleDescriptions.has(description) ? 'block' : 'none';
        if (description.style.display !== display || description.style.getPropertyPriority?.('display')) {
          description.style.display = display;
        }
      }
    }
  }

  const stickyRecords = new Map();
  const materialRecords = new Map();
  const optionRecords = new Map();

  function findProductRoots(scope) {
    const roots = [];
    if (scope.matches?.('[data-artbox-product]')) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.('[data-artbox-product]') ?? []));
    return roots;
  }

  function updateSticky(record) {
    if (!record.button) return;
    const mobile = Boolean(record.mediaQuery?.matches);
    const entry = record.lastEntry;
    const currentSentinelTop = mobile ? record.sentinel?.getBoundingClientRect?.().top : undefined;
    const isAboveViewport = Number.isFinite(currentSentinelTop)
      ? currentSentinelTop < 0
      : entry && !entry.isIntersecting && entry.boundingClientRect?.top < 0;
    const viewportHeight = window.innerHeight ?? 0;
    const currentStoryTop = mobile ? record.storySource?.getBoundingClientRect?.().top : undefined;
    const storyStarted = Number.isFinite(currentStoryTop)
      ? currentStoryTop < viewportHeight
      : record.storyEntry ? record.storyEntry.isIntersecting : true;
    const currentEndTop = mobile ? record.endBoundary?.getBoundingClientRect?.().top : undefined;
    if (mobile && Number.isFinite(currentEndTop) && viewportHeight > 0) {
      if (record.stickyEndReached) {
        if (currentEndTop >= viewportHeight * 0.78) record.stickyEndReached = false;
      } else if (currentEndTop <= viewportHeight * 0.58) {
        record.stickyEndReached = true;
      }
    } else if (mobile && record.endEntry) {
      record.stickyEndReached = Boolean(
        record.endEntry.isIntersecting || record.endEntry.boundingClientRect?.top < viewportHeight,
      );
    }
    const endReached = Boolean(record.stickyEndReached);
    const sticky = Boolean(mobile && isAboveViewport && storyStarted && !endReached);
    const dockButton = Boolean(mobile && isAboveViewport && storyStarted);
    const ownerDocument = record.root.ownerDocument ?? document;
    const hasFinePointer = record.mobileBuybar && (window.matchMedia?.('(pointer: fine)')?.matches ?? false);
    const previewBarHeight = hasFinePointer
      ? ownerDocument.querySelector?.('#PBarNextFrameWrapper')?.getBoundingClientRect?.().height ?? 0
      : 0;

    if (record.lastSticky !== sticky) record.button.classList.toggle('is-artbox-sticky', sticky);
    positionEasySellButton(record, dockButton);
    if (record.mobileBuybar) {
      if (record.lastPreviewBarHeight !== previewBarHeight) {
        ownerDocument.body?.style.setProperty('--artbox-preview-bar-height', `${previewBarHeight}px`);
        record.mobileBuybar.style.setProperty('--artbox-preview-bar-height', `${previewBarHeight}px`);
        record.lastPreviewBarHeight = previewBarHeight;
      }
      if (record.lastSticky !== sticky) {
        record.mobileBuybar.hidden = !sticky;
        record.mobileBuybar.classList.toggle('is-artbox-sticky', sticky);
      }
    }
    record.lastSticky = sticky;
  }

  function scheduleStickyUpdate(record) {
    if (!record.button || record.scrollFrame != null) return;
    if (typeof window.requestAnimationFrame !== 'function') {
      updateSticky(record);
      return;
    }
    record.scrollFrame = window.requestAnimationFrame(() => {
      record.scrollFrame = null;
      updateSticky(record);
    });
  }

  function findDeviceOption(root) {
    return Array.from(root.querySelectorAll?.('[data-swatch-option]') ?? []).find((option) => {
      const title = option.querySelector('.t4s-swatch__title')?.textContent?.split(':')[0];
      return normalizeOptionName(title) === 'device';
    }) ?? null;
  }

  function selectedDeviceValue(option) {
    return option?.querySelector('[data-swatch-item].is--selected')?.getAttribute('data-value')?.trim() ?? '';
  }

  function deviceIsConfirmed(root) {
    const confirmation = optionRecords.get(root);
    if (confirmation) {
      return confirmation.deviceConfirmed && Boolean(selectedDeviceValue(findDeviceOption(root)));
    }
    return (root.dataset.artboxDeviceConfirmation !== 'required'
      && !(incomingNavigationRequiresDevice && findDeviceOption(root))) || incomingVariantConfirmsDevice;
  }

  function deviceDropdownIsOpen(option) {
    const dropdown = option?.querySelector?.('[data-dropdown-open]');
    const wrapperId = dropdown?.getAttribute?.('data-id');
    const ownerDocument = option?.ownerDocument ?? document;
    const wrapper = wrapperId
      ? ownerDocument.getElementById?.(wrapperId)
      : option?.querySelector?.('[data-dropdown-wrapper]');
    return Boolean(
      dropdown?.classList?.contains?.('is--clicked')
      || wrapper?.classList?.contains?.('is--opened'),
    );
  }

  function syncMobileDevicePlaceholder(record) {
    if (!record?.mobileDevice) return;
    const confirmed = deviceIsConfirmed(record.root);
    let placeholder = record.mobileDevice.querySelector?.('[data-artbox-device-placeholder]');
    if (!placeholder && typeof document.createElement === 'function') {
      placeholder = document.createElement('option');
      placeholder.dataset.artboxDevicePlaceholder = '';
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.textContent = record.root.dataset.artboxDevicePlaceholder || 'Select your model';
      record.mobileDevice.insertBefore?.(placeholder, record.mobileDevice.firstChild ?? null);
    }
    if (placeholder) placeholder.hidden = confirmed;
    if (!confirmed) {
      record.mobileDevice.value = '';
      if (placeholder) placeholder.selected = true;
    }
  }

  function renderDeviceConfirmation(record) {
    const option = findDeviceOption(record.root);
    if (!option) {
      syncColorChoices(record);
      return;
    }

    if (!selectedDeviceValue(option)) record.deviceConfirmed = false;
    const deviceRequired = !record.deviceConfirmed;
    if (option.classList?.contains?.('is-artbox-device-required') !== deviceRequired) {
      option.classList?.toggle?.('is-artbox-device-required', deviceRequired);
    }
    const deviceInvalid = record.devicePrompted && deviceRequired;
    if (deviceInvalid && option.getAttribute?.('aria-invalid') !== 'true') {
      option.setAttribute?.('aria-invalid', 'true');
    } else if (!deviceInvalid && option.hasAttribute?.('aria-invalid')) {
      option.removeAttribute?.('aria-invalid');
    }

    syncUnconfirmedPurchaseControls(record, deviceRequired);
    if (deviceRequired) syncUnconfirmedPrice(record);
    const stickyRecord = stickyRecords.get(record.root);
    syncMobileDevicePlaceholder(stickyRecord);
    syncMobileBuybarDevice(stickyRecord);
    syncColorChoices(record);
  }

  function confirmDevice(record) {
    if (!record || record.deviceConfirmed) return;
    record.deviceConfirmed = true;
    record.devicePrompted = false;
    record.devicePickerRequested = false;
    delete record.root.dataset.artboxPurchaseDeviceRequested;
    window.clearTimeout?.(record.deviceNudgeTimer);
    window.clearTimeout?.(record.devicePickerTimer);
    record.devicePickerPending = false;
    record.root.classList?.remove?.('is-artbox-device-picker-launching');
    waitForKallesUpdate().then(() => {
      if (optionRecords.get(record.root) === record) renderDeviceConfirmation(record);
    });
  }

  function scheduleDeviceDropdownOpen(record, option, dropdown, afterNudge = false) {
    const open = () => {
      record.devicePickerFrame = null;
      record.deviceNudgeTimer = null;
      window.clearTimeout?.(record.devicePickerTimer);
      record.devicePickerTimer = null;
      record.devicePickerPending = false;
      record.root.classList?.remove?.('is-artbox-device-picker-launching');
      if (optionRecords.get(record.root) !== record
        || deviceIsConfirmed(record.root)
        || deviceDropdownIsOpen(option)) return;
      if (afterNudge && dropdown?.dispatchEvent && typeof window.MouseEvent === 'function') {
        const click = new window.MouseEvent('click', { bubbles: true, cancelable: true, view: window });
        // Use the theme's forwarded-touch contract so FastClick does not suppress this intentional click.
        click.forwardedTouchEvent = true;
        dropdown.dispatchEvent(click);
      } else {
        dropdown?.click?.();
      }
      record.devicePickerGuardUntil = Date.now() + 320;
    };
    if (afterNudge) {
      window.clearTimeout?.(record.deviceNudgeTimer);
      record.deviceNudgeTimer = window.setTimeout(open, 300);
      return;
    }
    record.devicePickerFrame = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(open)
      : window.setTimeout?.(open, 0);
  }

  function promptForDevice(record) {
    const option = findDeviceOption(record.root);
    if (!option) return;
    const dropdown = option.querySelector('[data-dropdown-open]');
    const featured = record.root.matches?.('[data-product-featured]')
      ? record.root
      : record.root.querySelector?.('[data-product-featured]');

    record.devicePrompted = true;
    renderDeviceConfirmation(record);
    const mobile = window.matchMedia?.('(max-width: 767px)')?.matches ?? false;
    const stickyRecord = stickyRecords.get(record.root);
    const mobileSelect = stickyRecord?.mobileDevice;
    const stickyPicker = stickyRecord?.mobileBuybar?.classList.contains('is-artbox-sticky') && mobileSelect;
    const nudge = stickyPicker ? mobileSelect : option;
    nudge.classList?.remove?.('is-artbox-device-nudging');
    void nudge.offsetWidth;
    nudge.classList?.add?.('is-artbox-device-nudging');
    if (featured && !featured.classList.contains('initProducts__enabled') && record.root.dataset.artboxVariantsReady !== 'true') {
      record.devicePickerRequested = true;
      return;
    }

    record.devicePickerRequested = false;
    if (deviceDropdownIsOpen(option)) {
      scheduleDeviceDropdownOpen(record, option, dropdown);
      return;
    }
    if (record.devicePickerPending) {
      return;
    }
    record.devicePickerPending = true;
    record.root.classList?.add?.('is-artbox-device-picker-launching');
    window.clearTimeout?.(record.devicePickerTimer);
    record.devicePickerTimer = window.setTimeout?.(() => {
      record.devicePickerPending = false;
      record.devicePickerTimer = null;
      record.root.classList?.remove?.('is-artbox-device-picker-launching');
    }, 900);

    scheduleDeviceDropdownOpen(record, option, dropdown, mobile);
  }

  function syncMobileBuybarDevice(record) {
    if (!record?.mobileDevice) return;
    const option = findDeviceOption(record.root);
    if (!option) return;

    const items = Array.from(option.querySelectorAll('[data-swatch-item]'));
    const selected = items.find((item) => item.classList.contains('is--selected'));
    if (selected && deviceIsConfirmed(record.root)) {
      record.mobileDevice.value = selected.getAttribute('data-value')?.trim() ?? '';
    }

    for (const choice of Array.from(record.mobileDevice.options ?? [])) {
      const nativeItem = items.find((item) => item.getAttribute('data-value')?.trim() === choice.value);
      choice.disabled = Boolean(!nativeItem
        || nativeItem.classList.contains('is--soldout')
        || nativeItem.classList.contains('is--unavailable'));
    }
    syncMobileDevicePlaceholder(record);
    if (record.mobileBuyFallback) {
      record.mobileBuyFallback.disabled = Boolean(record.button?.disabled
        || record.button?.getAttribute?.('aria-disabled') === 'true');
    }
  }

  function initialiseMobileBuybar(record) {
    const buybar = record.root.querySelector?.('[data-artbox-mobile-buybar]');
    if (!buybar) return;

    record.mobileBuybar = buybar;
    record.mobileDevice = buybar.querySelector('[data-artbox-mobile-device]');
    record.mobileBuySlot = buybar.querySelector('[data-artbox-mobile-buy-slot]');
    record.mobileBuyFallback = buybar.querySelector('[data-artbox-mobile-buy-fallback]');
    if (buybar.dataset.artboxMobileBuybarReady === 'true') return;
    buybar.dataset.artboxMobileBuybarReady = 'true';

    record.mobileDevice?.addEventListener('change', () => {
      const option = findDeviceOption(record.root);
      const target = Array.from(option?.querySelectorAll?.('[data-swatch-item]') ?? []).find((item) =>
        item.getAttribute('data-value')?.trim() === record.mobileDevice.value
        && !item.classList.contains('is--soldout')
        && !item.classList.contains('is--unavailable'));
      if (target) {
        target.click();
        confirmDevice(optionRecords.get(record.root));
      }
      waitForKallesUpdate().then(() => syncMobileBuybarDevice(record));
    });
  }

  function positionEasySellButton(record, sticky) {
    if (!record?.button || !record.mobileBuySlot) return;

    if (sticky) {
      if (record.button.parentNode !== record.mobileBuySlot) record.mobileBuySlot.appendChild(record.button);
      if (record.mobileBuyFallback) record.mobileBuyFallback.hidden = true;
      return;
    }

    const home = record.sentinel?.parentNode;
    if (home && record.button.parentNode === record.mobileBuySlot) {
      home.insertBefore(record.button, record.sentinel.nextSibling);
    }
    if (record.mobileBuyFallback) record.mobileBuyFallback.hidden = false;
  }

  function syncMaterialChoiceState(root) {
    const choices = Array.from(root.querySelectorAll('.artbox-material-option__choice'));
    let selectedChoice = null;
    let firstAvailableChoice = null;

    for (const choice of choices) {
      const selected = choice.classList.contains('is--selected');
      const unavailable = choice.classList.contains('is--soldout')
        || choice.classList.contains('is--unavailable');
      choice.setAttribute('aria-checked', String(selected));
      choice.setAttribute('aria-disabled', String(unavailable));
      choice.disabled = unavailable;
      choice.tabIndex = -1;
      if (!unavailable && !firstAvailableChoice) firstAvailableChoice = choice;
      if (selected && !unavailable) selectedChoice = choice;
    }

    const tabStop = selectedChoice || firstAvailableChoice;
    if (tabStop) tabStop.tabIndex = 0;
  }

  function initialiseMaterialKeyboard(root) {
    for (const group of root.querySelectorAll('.artbox-material-option__choices')) {
      if (group.dataset.artboxMaterialKeyboard === 'true') continue;
      group.dataset.artboxMaterialKeyboard = 'true';

      group.addEventListener('keydown', (event) => {
        const currentChoice = event.target;
        if (!currentChoice?.classList?.contains('artbox-material-option__choice')) return;

        const choices = Array.from(group.querySelectorAll('.artbox-material-option__choice'))
          .filter((choice) => !choice.disabled);
        const currentIndex = choices.indexOf(currentChoice);
        if (currentIndex === -1 || choices.length === 0) return;

        let nextIndex;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (currentIndex - 1 + choices.length) % choices.length;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % choices.length;
        }
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = choices.length - 1;
        if (nextIndex === undefined) return;

        event.preventDefault();
        const nextChoice = choices[nextIndex];
        nextChoice.focus();
        nextChoice.click();
      });
    }
  }

  function enhanceMaterialChoices(root) {
    const choices = root.querySelectorAll('.artbox-material-option__choice');
    if (choices.length === 0) return;

    syncMaterialChoiceState(root);
    initialiseMaterialKeyboard(root);
    if (materialRecords.has(root) || typeof window.MutationObserver !== 'function') return;

    const record = { root };
    record.observer = new window.MutationObserver(() => {
      syncMaterialChoiceState(root);
      initialiseMaterialKeyboard(root);
    });
    record.observer.observe(root, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
    materialRecords.set(root, record);
  }

  function readColorVariantData(record) {
    if (record.colorVariantData) return record.colorVariantData;
    const products = new Map();
    try {
      const data = JSON.parse(record.root.querySelector('[data-artbox-color-variants]')?.textContent || '[]');
      for (const product of data) {
        if (Array.isArray(product.options) && Array.isArray(product.variants)) {
          products.set(String(product.id), product);
        }
      }
    } catch (_error) {
      // A missing or malformed optional payload must not block product controls.
    }
    record.colorVariantData = products;
    return products;
  }

  function syncColorChoices(record) {
    if (!record) return;
    const products = readColorVariantData(record);
    if (products.size === 0) return;
    const selected = new Map();
    for (const option of record.root.querySelectorAll('[data-swatch-option]')) {
      const name = option.querySelector('.t4s-swatch__title')?.textContent?.split(':')[0];
      const value = option.querySelector('[data-swatch-item].is--selected')?.getAttribute('data-value')?.trim();
      if (value) selected.set(normalizeOptionName(name), value);
    }
    if (!selected.has('case type') && !selected.has('device')) return;

    const scopes = [record.root, record.root.querySelector('[data-artbox-all-colors-template]')?.content].filter(Boolean);
    const matches = (product, variant, ignoreDevice = false) => product.options.every((name, index) => {
      const key = normalizeOptionName(name);
      if (['color', 'colors', 'colour', 'colours'].includes(key)) return true;
      if (key === 'device' && (!record.deviceConfirmed || ignoreDevice)) return true;
      const choice = selected.get(key);
      return !choice || String(variant[1]?.[index] || '').trim() === choice;
    });

    for (const scope of scopes) for (const link of scope.querySelectorAll('a[data-artbox-color-product]')) {
      const product = products.get(link.dataset.artboxColorProduct);
      if (!product || !product.options.some((name) => ['case type', 'device'].includes(normalizeOptionName(name)))) continue;
      const candidates = product.variants.filter((variant) => matches(product, variant));
      const variant = candidates.find((candidate) => candidate[2]) || candidates[0];
      if (!variant) {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.tabIndex = -1;
        continue;
      }

      try {
        const url = new URL(product.url, document.baseURI);
        url.searchParams.set('variant', String(variant[0]));
        if (record.deviceConfirmed) {
          url.searchParams.delete('artbox_choose_device');
          if (selected.has('device')) url.searchParams.set('artbox_device', selected.get('device'));
        } else {
          url.searchParams.set('artbox_choose_device', '1');
          url.searchParams.delete('artbox_device');
        }
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const href = url.pathname + url.search + url.hash;
        if (link.getAttribute('href') !== href) link.setAttribute('href', href);
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      } catch (_error) {
        continue;
      }

      const sameTypeImage = product.variants.find((candidate) => matches(product, candidate, true) && product.images?.[candidate[3]]);
      const source = product.images?.[variant[3]] || product.images?.[sameTypeImage?.[3]];
      const image = link.querySelector('img');
      if (!source || !image) continue;
      const large = new URL(source, document.baseURI);
      const small = new URL(large);
      small.searchParams.set('width', '102');
      const srcset = `${small.href} 102w, ${large.href} 204w`;
      if (image.hasAttribute('data-color-src')) {
        if (image.dataset.colorSrc !== large.href) image.dataset.colorSrc = large.href;
        continue;
      }
      if (image.getAttribute('srcset') !== srcset) image.setAttribute('srcset', srcset);
      if (image.getAttribute('src') !== large.href) image.setAttribute('src', large.href);
    }
    const dialog = record.root.querySelector('.artbox-all-colors[open]');
    if (dialog) prepareAllColorImages(dialog);
  }

  function enhanceRememberedOptions(root) {
    if (optionRecords.has(root)) return;

    root.dataset.artboxOptionsReady = 'pending';
    const record = {
      root,
      deviceConfirmed: (root.dataset.artboxDeviceConfirmation !== 'required'
        && !(incomingNavigationRequiresDevice && findDeviceOption(root))) || incomingVariantConfirmsDevice || root.dataset.artboxEarlyDeviceConfirmed === 'true',
      devicePrompted: false,
    };
    record.handleDevicePickerGuard = (event) => {
      if (!record.devicePickerGuardUntil || Date.now() >= record.devicePickerGuardUntil) return;
      if (event.target?.closest?.('[data-dropdown-wrapper]')) return;
      const option = findDeviceOption(root);
      if (!option || !deviceDropdownIsOpen(option)) return;
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
      event.stopPropagation?.();
    };
    record.handlePurchaseClick = (event) => {
      const purchase = event.target?.closest?.('#es-popup-button, [data-atc-form]');
      if (!purchase || !root.contains?.(purchase) || deviceIsConfirmed(root)) return;
      record.deviceConfirmed = false;
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
      event.stopPropagation?.();
      event.originalEvent?.preventDefault?.();
      event.originalEvent?.stopImmediatePropagation?.();
      event.originalEvent?.stopPropagation?.();
      promptForDevice(record);
    };
    record.handleOptionEvent = (event) => {
      if (event.target?.matches?.('select[name="id"], input[name="id"]')) {
        waitForKallesUpdate().then(() => {
          if (optionRecords.get(root) === record) rememberViewedColor(record);
        });
        return;
      }
      const option = event.target?.closest?.('[data-swatch-option]');
      if (!option || !root.contains?.(option)) return;

      const title = option.querySelector('.t4s-swatch__title')?.textContent?.split(':')[0];
      const selectedItem = event.target?.closest?.('[data-swatch-item]');
      const isDeviceSelection = normalizeOptionName(title) === 'device' && selectedItem && !record.restoringCollectionDevice;

      renderDeviceConfirmation(record);

      waitForKallesUpdate().then(() => {
        if (optionRecords.get(root) !== record) return;
        if (isDeviceSelection && selectedDeviceValue(option)) {
          confirmDevice(record);
          syncSelectedVariantUrl(root);
        }
        rememberSelectedOption(option);
        syncConditionalDescriptions(root);
        syncPurchaseLabels(root);
        renderDeviceConfirmation(record);
        syncMobileBuybarDevice(stickyRecords.get(root));
        rememberViewedColor(record);
      });
      window.clearTimeout?.(record.deviceReconcileTimer);
      record.deviceReconcileTimer = window.setTimeout?.(() => {
        if (optionRecords.get(root) === record) renderDeviceConfirmation(record);
      }, 120);
    };
    root.addEventListener?.('click', record.handlePurchaseClick, true);
    record.handlePurchaseDeviceRequest = () => promptForDevice(record);
    root.addEventListener?.('artbox:request-device', record.handlePurchaseDeviceRequest);
    record.handleEarlyOptionChange = () => {
      if (root.dataset.artboxEarlyDeviceConfirmed === 'true') confirmDevice(record);
      syncConditionalDescriptions(root);
      syncPurchaseLabels(root);
      renderDeviceConfirmation(record);
      syncMobileBuybarDevice(stickyRecords.get(root));
      rememberViewedColor(record);
    };
    root.addEventListener?.('artbox:early-option-changed', record.handleEarlyOptionChange);
    root.addEventListener?.('artbox:variant-handoff', record.handleEarlyOptionChange);
    root.addEventListener?.('click', record.handleOptionEvent);
    root.addEventListener?.('change', record.handleOptionEvent);
    const ownerDocument = root.ownerDocument ?? document;
    ownerDocument.addEventListener?.('pointerdown', record.handleDevicePickerGuard, true);
    ownerDocument.addEventListener?.('mousedown', record.handleDevicePickerGuard, true);
    ownerDocument.addEventListener?.('touchstart', record.handleDevicePickerGuard, true);
    ownerDocument.addEventListener?.('click', record.handleDevicePickerGuard, true);
    optionRecords.set(root, record);
    syncColorChoices(record);
    if (readColorVariantData(record).size > 0 && typeof window.MutationObserver === 'function') {
      record.colorOptionObserver = new window.MutationObserver(() => syncColorChoices(record));
      for (const option of root.querySelectorAll('[data-swatch-option]')) {
        record.colorOptionObserver.observe(option, { attributes: true, attributeFilter: ['class'], subtree: true });
      }
    }

    const featured = root.matches?.('[data-product-featured]')
      ? root
      : root.querySelector('[data-product-featured]');
    if (!featured) return;

    const restoreWhenReady = () => {
      if (optionRecords.get(root) !== record || record.restoreStarted
        || (!featured.classList.contains('initProducts__enabled') && root.dataset.artboxVariantsReady !== 'true')) return;

      record.restoreStarted = true;
      record.readinessObserver?.disconnect();
      record.readinessObserver = null;
      // Kalles adds the marker and binds swatch listeners in one synchronous stack.
      // MutationObserver delivery happens after that stack has finished.
      restoreRememberedOptions(root, record).then(() => {
        if (optionRecords.get(root) !== record) return;
        syncConditionalDescriptions(root);
        syncPurchaseLabels(root);
        renderDeviceConfirmation(record);
        root.dataset.artboxOptionsReady = 'true';
        rememberViewedColor(record);
        if (root.dataset.artboxPurchaseDeviceRequested === 'true') {
          record.devicePickerRequested = true;
        }
        const reopenDevicePicker = record.devicePickerRequested && !deviceIsConfirmed(record.root);
        delete root.dataset.artboxPurchaseDeviceRequested;
        if (reopenDevicePicker) promptForDevice(record);
      });
    };

    record.handleVariantsReady = restoreWhenReady;
    root.addEventListener?.('artbox:variants-ready', record.handleVariantsReady);
    if (featured.classList.contains('initProducts__enabled') || root.dataset.artboxVariantsReady === 'true') {
      restoreWhenReady();
    } else if (typeof window.MutationObserver === 'function') {
      record.readinessObserver = new window.MutationObserver(restoreWhenReady);
      record.readinessObserver.observe(featured, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function observeEasySellPosition(record) {
    if (typeof window.IntersectionObserver !== 'function' || typeof window.matchMedia !== 'function') return;

    record.mediaQuery = window.matchMedia('(max-width: 767px)');
    record.mediaListener = () => scheduleStickyUpdate(record);
    record.intersectionObserver = new window.IntersectionObserver((entries) => {
      record.lastEntry = entries[0];
      scheduleStickyUpdate(record);
    });
    record.intersectionObserver.observe(record.sentinel);

    const ownerDocument = record.root.ownerDocument ?? document;
    const productId = record.root.dataset.artboxProductId;
    const storySources = ownerDocument.querySelectorAll?.('[data-artbox-story-source]') ?? [];
    record.storySource = Array.from(storySources)
      .find((source) => source.dataset.artboxProductId === productId) ?? null;
    record.endBoundary = ownerDocument.querySelector?.('[data-artbox-sticky-end]') ?? null;
    if (record.storySource || record.endBoundary) {
      record.boundaryObserver = new window.IntersectionObserver((entries) => {
        for (const boundaryEntry of entries) {
          if (boundaryEntry.target === record.storySource) record.storyEntry = boundaryEntry;
          if (boundaryEntry.target === record.endBoundary) record.endEntry = boundaryEntry;
        }
        scheduleStickyUpdate(record);
      });
      if (record.storySource) record.boundaryObserver.observe(record.storySource);
      if (record.endBoundary) record.boundaryObserver.observe(record.endBoundary);
    }

    record.scrollListener = () => scheduleStickyUpdate(record);
    window.addEventListener?.('scroll', record.scrollListener, { passive: true });
    window.addEventListener?.('resize', record.scrollListener, { passive: true });

    if (typeof record.mediaQuery.addEventListener === 'function') {
      record.mediaQuery.addEventListener('change', record.mediaListener);
    } else {
      record.mediaQuery.addListener?.(record.mediaListener);
    }
  }

  function removeSentinel(sentinel) {
    if (!sentinel) return;
    if (typeof sentinel.remove === 'function') {
      sentinel.remove();
    } else {
      sentinel.parentNode?.removeChild?.(sentinel);
    }
  }

  function releaseEasySell(record) {
    record.intersectionObserver?.disconnect();
    record.boundaryObserver?.disconnect();
    window.removeEventListener?.('scroll', record.scrollListener);
    window.removeEventListener?.('resize', record.scrollListener);
    if (record.scrollFrame != null) window.cancelAnimationFrame?.(record.scrollFrame);
    positionEasySellButton(record, false);
    record.button?.classList.remove('is-artbox-sticky');
    if (record.mobileBuybar) {
      record.mobileBuybar.hidden = true;
      record.mobileBuybar.classList.remove('is-artbox-sticky');
    }

    if (record.mediaQuery && record.mediaListener) {
      if (typeof record.mediaQuery.removeEventListener === 'function') {
        record.mediaQuery.removeEventListener('change', record.mediaListener);
      } else {
        record.mediaQuery.removeListener?.(record.mediaListener);
      }
    }

    removeSentinel(record.sentinel);
    if (record.button) delete record.button.dataset.artboxStickyReady;
    record.button = null;
    record.sentinel = null;
    record.intersectionObserver = null;
    record.boundaryObserver = null;
    record.mediaQuery = null;
    record.mediaListener = null;
    record.lastEntry = null;
    record.storyEntry = null;
    record.endEntry = null;
    record.storySource = null;
    record.endBoundary = null;
    record.scrollListener = null;
    record.scrollFrame = null;
    record.stickyEndReached = false;
    record.lastSticky = undefined;
    record.lastPreviewBarHeight = undefined;
  }

  function ensureReplacementObserver(record) {
    if (record.replacementObserver || typeof window.MutationObserver !== 'function') return;

    record.replacementObserver = new window.MutationObserver((mutations) => {
      const button = record.root.querySelector('#es-popup-button');
      if (button === record.button) {
        const buttonChanged = mutations.some((mutation) => (
          mutation.target === record.button
          || record.button?.contains?.(mutation.target)
        ));
        if (!buttonChanged) return;
        const optionRecord = optionRecords.get(record.root);
        if (optionRecord && !deviceIsConfirmed(record.root)) renderDeviceConfirmation(optionRecord);
        return;
      }

      releaseEasySell(record);
      if (button) attachEasySell(record, button);
    });
    record.replacementObserver.observe(record.root, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  function attachEasySell(record, button) {
    if (!button) return false;

    const sentinel = document.createElement('span');
    sentinel.dataset.artboxEasysellSentinel = '';
    sentinel.setAttribute('aria-hidden', 'true');

    if (typeof button.before === 'function') {
      button.before(sentinel);
    } else if (button.parentNode) {
      button.parentNode.insertBefore(sentinel, button);
    } else {
      return false;
    }

    button.dataset.artboxStickyReady = 'true';
    const buttonTitle = button.querySelector?.('.es-button-title');
    if (buttonTitle && buttonTitle.textContent !== 'Buy Now') buttonTitle.textContent = 'Buy Now';
    record.button = button;
    record.sentinel = sentinel;
    initialiseMobileBuybar(record);
    syncMobileBuybarDevice(record);
    observeEasySellPosition(record);
    ensureReplacementObserver(record);
    return true;
  }

  function enhanceEasySell(root) {
    if (stickyRecords.has(root)) return;

    const record = { root };
    stickyRecords.set(root, record);
    initialiseMobileBuybar(record);

    const attach = () => attachEasySell(record, root.querySelector('#es-popup-button'));

    if (attach() || typeof window.MutationObserver !== 'function') return;

    record.pendingObserver = new window.MutationObserver(() => {
      if (!attach()) return;
      record.pendingObserver.disconnect();
      record.pendingObserver = null;
    });
    record.pendingObserver.observe(root, { childList: true, subtree: true });
  }

  function prepareAllColorImages(dialog) {
    const options = dialog.querySelector('.artbox-all-colors__options');
    if (!options) return;
    let record = allColorsImageBatches.get(dialog);
    if (!record) {
      record = { ready: new Map(), signature: null };
      allColorsImageBatches.set(dialog, record);
    }
    if (!dialog.open || !dialog.isConnected) {
      record.cancel?.();
      record.signature = null;
      dialog.dataset.artboxColorImages = 'idle';
      options.removeAttribute('aria-busy');
      return;
    }
    const images = Array.from(options.querySelectorAll('[data-color-src]'));
    for (const image of images) {
      if (record.ready.get(image) !== image.dataset.colorSrc && image.dataset.artboxColorImage === 'ready') {
        image.dataset.artboxColorImage = 'pending';
      }
    }
    const bounds = options.getBoundingClientRect();
    const visible = images.filter((image) => {
      const rect = image.getBoundingClientRect();
      return rect.bottom > bounds.top && rect.top < bounds.bottom
        && rect.right > bounds.left && rect.left < bounds.right;
    });
    const signature = JSON.stringify(visible.map((image) => [images.indexOf(image), image.dataset.colorSrc]));
    if (record.signature === signature) return;
    record.cancel?.();
    record.signature = signature;
    const pending = visible.filter((image) => record.ready.get(image) !== image.dataset.colorSrc);
    if (pending.length === 0) {
      dialog.dataset.artboxColorImages = 'ready';
      options.removeAttribute('aria-busy');
      return;
    }
    dialog.dataset.artboxColorImages = 'loading';
    options.setAttribute('aria-busy', 'true');
    const batch = {};
    record.batch = batch;
    const cleanups = [];
    const results = new Map();
    let timer;
    let cancelled = false;
    const current = () => !cancelled && record.batch === batch && dialog.open && dialog.isConnected
      && pending.every((image) => image.dataset.colorSrc === batch.sources.get(image));
    batch.sources = new Map(pending.map((image) => [image, image.dataset.colorSrc]));
    const cleanup = () => {
      window.clearTimeout(timer);
      cleanups.forEach((dispose) => dispose());
    };
    record.cancel = () => { cancelled = true; cleanup(); };
    const finish = (image, ready) => {
      if (!current() || results.has(image)) return;
      results.set(image, ready);
      if (results.size !== pending.length) return;
      cleanup();
      for (const [item, loaded] of results) {
        item.dataset.artboxColorImage = loaded ? 'ready' : 'error';
        if (loaded) record.ready.set(item, batch.sources.get(item));
      }
      dialog.dataset.artboxColorImages = 'ready';
      options.removeAttribute('aria-busy');
    };
    timer = window.setTimeout(() => pending.forEach((image) => finish(image, false)), 6000);
    for (const image of pending) {
      let decoding = false;
      const loaded = () => {
        if (decoding || !current()) return;
        decoding = true;
        const decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
        Promise.resolve(decoded).then(() => finish(image, image.naturalWidth > 0), () => finish(image, false));
      };
      const failed = () => finish(image, false);
      image.addEventListener('load', loaded);
      image.addEventListener('error', failed);
      cleanups.push(() => {
        image.removeEventListener('load', loaded);
        image.removeEventListener('error', failed);
      });
      image.dataset.artboxColorImage = 'loading';
      image.loading = 'eager';
      image.decoding = 'async';
      image.fetchPriority = 'auto';
      image.removeAttribute('srcset');
      if (image.getAttribute('src') !== batch.sources.get(image) || (image.complete && image.naturalWidth === 0)) {
        image.src = batch.sources.get(image);
      }
      if (image.complete) {
        if (image.naturalWidth > 0) loaded();
        else failed();
      }
    }
  }

  function initialiseAllColors(root) {
    const trigger = root.querySelector('[data-artbox-all-colors-open]');
    const template = root.querySelector('[data-artbox-all-colors-template]');
    if (!trigger || !template || trigger.dataset.boundAllColors) return;
    trigger.dataset.boundAllColors = 'true';
    let dialog;
    let beginInteraction;
    let resizeObserver;
    trigger.addEventListener('click', (event) => {
      if (!dialog) {
        dialog = document.importNode(template.content.firstElementChild, true);
        root.append(dialog);
        syncColorChoices(optionRecords.get(root));
        const options = dialog.querySelector('.artbox-all-colors__options');
        options?.addEventListener('scroll', () => prepareAllColorImages(dialog), { passive: true });
        if (typeof window.ResizeObserver === 'function' && options) {
          resizeObserver = new window.ResizeObserver(() => prepareAllColorImages(dialog));
        }
        dialog.addEventListener('close', () => {
          resizeObserver?.disconnect();
          prepareAllColorImages(dialog);
        });
        dialog.querySelector('[data-artbox-all-colors-close]').addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          const bounds = dialog.getBoundingClientRect();
          if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
        });
        beginInteraction = bindProductDialogFocus(dialog, trigger);
      }
      beginInteraction(event);
      dialog.showModal();
      prepareAllColorImages(dialog);
      if (resizeObserver) {
        allColorsImageBatches.get(dialog).resizeObserver = resizeObserver;
        resizeObserver.observe(dialog.querySelector('.artbox-all-colors__options'));
      }
    });
  }

  function initialiseControls(scope) {
    findProductRoots(scope).forEach((root) => {
      initialiseLinkedColors(root);
      initialiseColorScrollbars(root);
      initialiseAllColors(root);
      enhanceColorNavigation(root);
      initialisePaymentDetails(root);
      enhanceMaterialChoices(root);
      enhanceRememberedOptions(root);
      syncPurchaseLabels(root);
    });
  }

  function initialise(scope) {
    initialiseGalleries(scope);
    initialiseControls(scope);
    findProductRoots(scope).forEach((root) => {
      syncConditionalDescriptions(root);
      enhanceEasySell(root);
    });
  }

  function clearSticky() {
    for (const record of stickyRecords.values()) {
      if (record.scrollFrame != null) window.cancelAnimationFrame?.(record.scrollFrame);
      record.scrollFrame = null;
      record.lastSticky = undefined;
      record.button?.classList.remove('is-artbox-sticky');
      if (record.mobileBuybar) {
        record.mobileBuybar.hidden = true;
        record.mobileBuybar.classList.remove('is-artbox-sticky');
      }
    }
  }

  function destroySticky(record) {
    record.pendingObserver?.disconnect();
    record.replacementObserver?.disconnect();
    releaseEasySell(record);
    stickyRecords.delete(record.root);
  }

  function unload(scope) {
    for (const dialog of scope.querySelectorAll?.('.artbox-all-colors') ?? []) {
      const batch = allColorsImageBatches.get(dialog);
      batch?.cancel?.();
      batch?.resizeObserver?.disconnect();
      if (dialog.open) dialog.close();
      allColorsImageBatches.delete(dialog);
    }
    releaseColorScrollbars(scope);
    for (const [rail, root] of colorLayoutRecords) {
      if (root !== scope && !scope.contains?.(root)) continue;
      colorLayoutObserver?.unobserve(rail);
      colorLayoutRecords.delete(rail);
    }
    if (colorLayoutRecords.size === 0 && colorLayoutFrame != null) {
      window.cancelAnimationFrame?.(colorLayoutFrame);
      colorLayoutFrame = null;
    }
    for (const [rail, record] of colorNavigationRecords) {
      if (record.root !== scope && !scope.contains?.(record.root)) continue;
      window.clearTimeout(record.timer);
      record.observer.disconnect();
      record.script?.remove();
      rail.removeEventListener('scroll', record.prepare);
      window.removeEventListener('load', record.prepare);
      colorNavigationRecords.delete(rail);
    }
    for (const record of stickyRecords.values()) {
      if (record.root === scope || scope.contains?.(record.root)) destroySticky(record);
    }
    for (const record of materialRecords.values()) {
      if (record.root !== scope && !scope.contains?.(record.root)) continue;
      record.observer?.disconnect();
      materialRecords.delete(record.root);
    }
    for (const record of optionRecords.values()) {
      if (record.root !== scope && !scope.contains?.(record.root)) continue;
      record.readinessObserver?.disconnect();
      record.colorOptionObserver?.disconnect();
      if (record.devicePickerFrame != null) {
        window.cancelAnimationFrame?.(record.devicePickerFrame);
        window.clearTimeout?.(record.devicePickerFrame);
      }
      window.clearTimeout?.(record.devicePickerTimer);
      window.clearTimeout?.(record.deviceNudgeTimer);
      window.clearTimeout?.(record.deviceReconcileTimer);
      record.root.classList?.remove?.('is-artbox-device-picker-launching');
      record.root.removeEventListener?.('click', record.handlePurchaseClick, true);
      record.root.removeEventListener?.('click', record.handleOptionEvent);
      record.root.removeEventListener?.('change', record.handleOptionEvent);
      record.root.removeEventListener?.('artbox:request-device', record.handlePurchaseDeviceRequest);
      record.root.removeEventListener?.('artbox:variants-ready', record.handleVariantsReady);
      record.root.removeEventListener?.('artbox:early-option-changed', record.handleEarlyOptionChange);
      record.root.removeEventListener?.('artbox:variant-handoff', record.handleEarlyOptionChange);
      const ownerDocument = record.root.ownerDocument ?? document;
      ownerDocument.removeEventListener?.('pointerdown', record.handleDevicePickerGuard, true);
      ownerDocument.removeEventListener?.('mousedown', record.handleDevicePickerGuard, true);
      ownerDocument.removeEventListener?.('touchstart', record.handleDevicePickerGuard, true);
      ownerDocument.removeEventListener?.('click', record.handleDevicePickerGuard, true);
      delete record.root.dataset.artboxOptionsReady;
      optionRecords.delete(record.root);
    }
  }

  const lifecycle = { clearSticky, initialise, unload };
  window[lifecycleKey] = lifecycle;
  document.addEventListener('shopify:section:load', (event) => lifecycle.initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => lifecycle.unload(event.target));
  window.addEventListener?.('pagehide', lifecycle.clearSticky);
  window.addEventListener?.('pageshow', () => {
    for (const record of optionRecords.values()) rememberViewedColor(record);
  });

  if (document.readyState === 'loading'
      || (document.readyState !== 'complete' && typeof window.jQuery_T4NT !== 'function')) {
    // The main section is complete before its async script. These controls do not
    // need later sections or the deferred Kalles bundle; full lifecycle keeps its order.
    initialiseControls(document);
    const completeInitialisation = () => {
      document.removeEventListener?.('DOMContentLoaded', completeInitialisation);
      window.removeEventListener?.('load', completeInitialisation);
      lifecycle.initialise(document);
    };
    document.addEventListener('DOMContentLoaded', completeInitialisation, { once: true });
    // An async script can arrive after DOMContentLoaded while the load event is pending.
    window.addEventListener?.('load', completeInitialisation, { once: true });
  } else {
    lifecycle.initialise(document);
  }
}());
})();
